import { makeAutoObservable } from "mobx";
import { DraftModelType, DraftParams, DraftTaskType } from "./types";
import { tasksStore, TaskType } from "@/stores/tasks";
import { v4 as uuidv4 } from "uuid";
import { userAssetsStore } from "@/stores/userAssets";
import { Editor_Words } from "@/utils/lang/editor";
import { addNewImage, toast } from "@/utils/misc";
import {
  addVirtualNode,
  postDrawRecord,
  recoverVirtualNode,
  VirtualNodeInfo,
} from "@/utils/safeOp";
import { useToast } from "@chakra-ui/react";
import { translate } from "@noli/business";
import { Lang, sleep, shallowCopy, getRandomHash } from "@noli/core";
import { fetchTaskStatus } from "../api";
import { postDraftTask } from "./api";
import { TaskStatusType, IWithResponse } from "../types";
import { polling } from "@/utils/loopRequestResult";
import { DraftMeta } from "./meta";
import { Draft_Words } from "./lang";

export const SIZE_MAP: {
  [key: string]: {
    [key: number]: {
      w: number;
      h: number;
    };
  };
} = {
  "1:1": {
    512: {
      w: 512,
      h: 512,
    },
    768: {
      w: 768,
      h: 768,
    },
    1024: {
      w: 1024,
      h: 1024,
    },
  },
  "3:4": {
    512: {
      w: 512,
      h: 768,
    },
    768: {
      w: 768,
      h: 1024,
    },
  },
  "4:3": {
    512: {
      w: 768,
      h: 512,
    },
    768: {
      w: 1024,
      h: 768,
    },
  },
};

export type SizeType = "1:1" | "4:3" | "3:4";
class DraftStore {
  taskType: DraftTaskType = DraftTaskType.txt2img;
  modelTpe: DraftModelType = DraftModelType.sd;
  text = "";
  url = "";
  size: SizeType = "1:1";
  resolution = 512;
  fidelity = 0.2;
  promptExpanding = false;
  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get wh(): { w: number; h: number } {
    const wh = SIZE_MAP[this.size][this.resolution];
    return wh
      ? wh
      : {
          w: 512,
          h: 512,
        };
  }

  get cost(): number {
    const { w, h } = this.wh;
    return w / 512 + h / 512;
  }

  setSize(size: SizeType) {
    this.size = size;
    if (size !== "1:1" && this.resolution === 1024) {
      this.resolution = 512;
    }
  }

  setResolution(r: number) {
    this.resolution = r;
  }

  setText(text: string) {
    this.text = text;
  }

  setTaskType(type: string) {
    if (type === DraftTaskType.img2img || type === DraftTaskType.txt2img) {
      this.taskType = type;
    } else {
      console.log("not support taskType(draft-setTaskType)");
    }
  }

  setModelType(model: string) {
    switch (model) {
      case DraftModelType.sd:
      case DraftModelType.sd_20:
      case DraftModelType.pixiv:
      case DraftModelType.pixiv_hand:
      case DraftModelType.indoor_design:
      case DraftModelType["architecture"]:
      case DraftModelType["comic"]:
      case DraftModelType["arcane"]:
      case DraftModelType["food"]:
      case DraftModelType["pixel"]:
      case DraftModelType["retro"]:
      case DraftModelType["photo"]:
      case DraftModelType["dream_art"]:
      case DraftModelType["oil_paint"]:
      case DraftModelType["acg"]:
      case DraftModelType["soft"]:
      case DraftModelType["3D"]:
      case DraftModelType["sam"]:
      case DraftModelType["inkpunk"]:
      case DraftModelType["petal"]:
      case DraftModelType["3D_icon"]:
      case DraftModelType["cartoon_wallpaper"]:
      case DraftModelType["character_simulation"]:
      case DraftModelType["antique_photo"]:
      case DraftModelType["crayon"]:
      case DraftModelType["colorful_art"]:
      case DraftModelType["2_5D_antique"]:
        // case DraftModelType.game_icon:
        this.modelTpe = model;
        break;
      default:
        console.log("not support model type");
        break;
    }
  }

  setUrl(url: string) {
    this.url = url;
  }

  setFidelity(fidelity: number) {
    this.fidelity = fidelity;
  }

  buildParams(): DraftParams {
    const { w, h } = this.wh;
    switch (this.taskType) {
      case DraftTaskType.txt2img:
        return {
          task: `${this.taskType}.${this.modelTpe}`,
          params: {
            text: this.text,
            w,
            h,
          },
        };
      case DraftTaskType.img2img:
        return {
          task: `${this.taskType}.${this.modelTpe}`,
          params: {
            text: this.text,
            w,
            h,
            fidelity: this.fidelity,
            url: this.url,
          },
        };
      default:
        throw new Error("error task type");
    }
  }

  setPromptExpanding(loading: boolean) {
    this.promptExpanding = loading;
  }

  async pollingTask(
    taskId: string,
    {
      t,
      lang,
      meta,
    }: {
      t: ReturnType<typeof useToast>;
      lang: Lang;
      meta: DraftMeta;
    },
  ) {
    // do some thing
    const createTime = Date.now() / 1000;
    const virtualInfo = meta.data.wh;
    const count = 1;
    const virtualNodeInfoList: VirtualNodeInfo[] = [];
    for (let i = 0; i < count; i++) {
      if (i === 0) {
        toast(t, "success", translate(Editor_Words["submit-success-message"], lang));
      }
      virtualNodeInfoList.push({
        alias: await addVirtualNode(t, ".draft.", lang, virtualInfo),
      });
      await sleep(500);
    }
    const taskType = meta.data.taskType;
    const task: TaskType = {
      key: uuidv4(),
      type: `draft${/txt2img/.test(taskType) ? "" : "-img2img"}`,
      status: "working",
      pending: 0,
      isKafka: false,
      taskPack: { uri: "", data: {} },
    };
    tasksStore.addTask(task);
    const failed = async () => {
      toast(t, "error", translate(Draft_Words["error-message"], lang));
      tasksStore.removeTask(task);
      for (let i = 0; i < count; i++) {
        await recoverVirtualNode(virtualNodeInfoList[i]);
      }
    };
    // query task
    const res = await polling(
      () => fetchTaskStatus(taskId),
      (res) => {
        const taskStatus = res.status as TaskStatusType;
        switch (taskStatus) {
          case "exception":
          case "not_found":
            return -1;
          case "pending":
          case "working":
            return 0;
          case "finished":
            return 1;
        }
      },
    ).catch(failed);
    const imgURLs: string[] = res.data.img_urls;
    for (let i = 0; i < count; i++) {
      const bbox = await recoverVirtualNode(virtualNodeInfoList[i]);
      if (!bbox) continue;
      const url = imgURLs[i];
      if (!url) continue;
      const newAlias = `draft.${taskId}.${getRandomHash()}`;
      const iMeta = shallowCopy(meta);
      iMeta.data.timestamp = Date.now();
      addNewImage(newAlias, url, {
        info: bbox,
        meta: iMeta,
        callbacks: {
          success: async () => {
            postDrawRecord(url, Date.now() / 1000 - createTime, iMeta);
            if (i === count - 1) {
              toast(t, "success", translate(Editor_Words["generate-success-message"], lang));
            }
          },
          failed: async () => console.error(`Failed to add Draft at ${i}`),
        },
      });
    }
    tasksStore.removeTask(task);
    await userAssetsStore.fetchHeartCoin();
  }

  async postTask({ t, lang }: { t: ReturnType<typeof useToast>; lang: Lang }) {
    const params = this.buildParams();
    const res: IWithResponse<{ uid: string }> = await postDraftTask(params);
    const meta: DraftMeta = {
      type: "draft",
      data: {
        wh: this.wh,
        taskType: params.task,
        ...params.params,
      },
    };
    if (res.code === 0) {
      this.pollingTask(res.data.uid, {
        t,
        lang,
        meta,
      });
    } else {
      if (res.msg === "balance not enough") {
        toast(t, "error", translate(Draft_Words["balance-not-enough-message"], lang));
      }
    }
  }
}

export const draftStore = new DraftStore();
export { DraftStore };
